import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useAtom } from 'jotai';
import { type SetStateAction, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { isSidebarExpandedAtom, submenuAtom } from '@/store/sidebar';

import useStyles from './SidebarSubmenuButton.styles';
import type {
  SidebarSubmenuButtonProps,
  SidebarSubmenuStateKey
} from './SidebarSubmenuButton.types';
import { SidebarSubmenuInline } from './SidebarSubmenuInline';
import { SidebarSubmenuPopup } from './SidebarSubmenuPopup';

function SidebarSubmenuButton(props: SidebarSubmenuButtonProps) {
  const { item } = props;
  const [isSidebarExpanded] = useAtom(isSidebarExpandedAtom);
  const [isSubmenuExpanded, setIsSubmenuExpanded] = useAtom(submenuAtom);
  const [anchorEl, setAnchorEl] = useState(null);
  const { classes } = useStyles({ isSidebarExpanded });
  const { pathname } = useLocation();

  const handleInternalMenu = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => {
    const { currentTarget } = e as React.MouseEvent<HTMLLIElement, MouseEvent>;

    if (isSubmenuExpanded.Internal) {
      setIsSubmenuExpanded({ Internal: false });
      setAnchorEl(null);
      return;
    }

    setIsSubmenuExpanded({ Internal: true });
    setAnchorEl(currentTarget as unknown as SetStateAction<null>);
  };

  const isSubMenuActive = useMemo(() => {
    return item?.menu?.some((subItem) => {
      if (subItem.pathTo === undefined) {
        return false;
      }
      return subItem.pathTo === pathname;
    });
  }, [pathname, item.menu]);

  useEffect(() => {
    if (!isSidebarExpanded && isSubmenuExpanded.Internal) {
      setIsSubmenuExpanded({ Internal: false });
    }
  }, [isSidebarExpanded]);

  return (
    <>
      <ListItem
        className={classes.submenuButton}
        disablePadding
        onClick={(e) => handleInternalMenu(e)}
      >
        <ListItemButton
          aria-label={item.ariaLabel}
          className={
            classes.submenuButtonButton + (isSubMenuActive ? ' active' : '')
          }
        >
          <ListItemIcon className={classes.submenuButtonIcon}>
            {item.component}
          </ListItemIcon>
          <ListItemText
            className={classes.submenuButtonText}
            primary={item.label}
          />
          <div className={classes.submenuButtonSubmenuIcon}>
            {isSidebarExpanded &&
              (isSubmenuExpanded[item.label as keyof SidebarSubmenuStateKey] ? (
                <FontAwesomeIcon icon={faChevronUp} size="xs" />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} size="xs" />
              ))}
          </div>
        </ListItemButton>
      </ListItem>

      {/* submenu - sidebar expanded */}
      {isSidebarExpanded && item !== undefined && (
        <SidebarSubmenuInline item={item} />
      )}

      {/* submenu - sidebar collapsed */}
      {!isSidebarExpanded && item !== undefined && (
        <SidebarSubmenuPopup
          anchorEl={isSubmenuExpanded.Internal ? anchorEl : null}
          handleInternalMenu={handleInternalMenu}
          isSubmenuExpanded={isSubmenuExpanded}
          item={item}
        />
      )}
    </>
  );
}

export default SidebarSubmenuButton;
